import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, fetchApi, SERVER_BASE_URL } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";
import { isTauriRuntime } from "@/lib/platform";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@bso/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ProviderId = "google" | "apple";

const TRAILING_SLASH = /\/$/u;

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * Starting URL for the LINKING flow. `client` tells the server which client starts the link:
 * a browser gets its callback without the session token, while `native` keeps it for the
 * Tauri WebView, which authenticates with it. See `native_client` in the server's
 * `handlers/auth.rs`. Hoisted to module scope for oxlint.
 */
function linkUrl(provider: ProviderId): string {
  const base = `${SERVER_BASE_URL.replace(TRAILING_SLASH, "")}/api/auth/link-social/${provider}`;
  const client = isTauriRuntime() ? "native" : "web";
  return `${base}?${new URLSearchParams({ client }).toString()}`;
}

/**
 * "Connected accounts": allows linking/unlinking Google and Apple from the
 * profile. It only shows providers ENABLED in the backend. It respects the
 * last-method safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 · P26: the two `queryFn`s did `data ?? {…}` over
  // `openapi-fetch`, which NEVER throws. A 503 resolved as SUCCESS with a
  // FABRICATED object, so `isError` was UNREACHABLE and the `QueryErrorState`
  // below had been dead since it was written. Measured with `login-methods`
  // returning 503: the card remained visible, `connected-accounts-error` was 0,
  // and the screen said "Not connected" + "This is your only sign-in method"
  // for an account whose methods had NOT been read.
  // `fetchApi` throws on any non-2xx: the error becomes an error again.
  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => fetchApi(sharedApi.socialProviders(api)),
    staleTime: SESSION_STALE_TIME_MS,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => fetchApi(sharedApi.loginMethods(api)),
    retry: false,
  }));

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: EAGER memo → read at mount, before everything
    // `<Show>`. `isSuccess` is the condition for `data` to exist.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2: without the guard, when `login-methods` failed the `?? []`
  // turned "I don't know" into "not linked", and the screen said **"Not
  // connected"** for a Google account that WAS linked — inviting the user
  // to link something they already have. The same applies to `has_password`,
  // which also feeds `isLastMethod` (the "don't get locked out" protection).
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  /**
   * Did EITHER of the card's two reads fail?
   *
   * P23/CC-60 · P26 — THE THIRD PIECE, and without it the other two do not change
   * the symptom. Making the `queryFn`s throw is NOT enough: on error
   * `enabledProviders()` is `[]`, and the OUTER `<Show>` hid the ENTIRE
   * card — the error box was still unreachable, just for another reason. The
   * card must exist even when it could not be read, which is exactly when the
   * user needs to know.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const retryAll = () => {
    void providersQuery.refetch();
    void methodsQuery.refetch();
  };

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const { message } = error;
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback after returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* El error cubre las DOS lecturas: si falla `providers` la tarjeta
              tampoco sabe qué botones ofrecer, y callarlo era justo el síntoma
              («Google · Not connected» de algo que no se ha podido leer). */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState testId="connected-accounts-error" compact onRetry={retryAll} />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
